import * as XLSX from 'xlsx'

import {
  appendStyledSheet,
  calculatePercent,
  capitalizeFirst,
  createReportHeader,
  formatMetric,
} from '@/shared/lib/dashboardExcelFormat'

/**
 * Build and download a professional, readable Excel file for the Marketing Dashboard.
 */
export function exportMarketingDashboardToExcel({
  campaignLeads,
  conversionRate,
  ordersClosed,
  ordersLost,
  leadsManagement,
  campaignPerformance = [],
  period = 'all',
  customRange = {},
} = {}) {
  const workbook = XLSX.utils.book_new()
  const exportedAt = new Date().toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })

  // ---------------------------------------------------------------------------
  // 1. MARKETING SUMMARY SHEET
  // ---------------------------------------------------------------------------
  const totalLeads = Number(campaignLeads?.total) || 0
  const convertedLeads =
    Number(conversionRate?.total) || Number(ordersClosed?.total) || 0
  const wonOrders =
    Number(ordersClosed?.total) || Number(conversionRate?.wonCount) || 0
  const lostOrders =
    Number(ordersLost?.total) || Number(conversionRate?.lostCount) || 0

  const conversionPercentage = conversionRate?.percentage
    ? `${conversionRate.percentage}%`
    : calculatePercent(convertedLeads, totalLeads)

  const summaryRows = [
    ...createReportHeader({
      title: 'Marketing Analytics Dashboard',
      subtitle:
        'Campaign Leads Generation, Conversions, and Channel Effectiveness',
      period,
      customRange,
      exportedAt,
    }),
    ['KEY PERFORMANCE INDICATORS (KPIs)'],
    [
      'Category',
      'Metric Name',
      'Performance Value',
      'Context & Benchmark Notes',
    ],
    [
      'Lead Generation',
      'Total Campaign Leads Generated',
      formatMetric(totalLeads, 'Leads'),
      capitalizeFirst(campaignLeads?.subtitle) ||
        'All campaign leads generated in period',
    ],
    [
      'Conversions',
      'Total Converted Leads',
      formatMetric(convertedLeads, 'Leads'),
      capitalizeFirst(conversionRate?.subtitle) ||
        'Leads successfully converted to orders',
    ],
    [
      'Conversions',
      'Marketing Conversion Rate',
      conversionPercentage,
      capitalizeFirst(conversionRate?.highlight) ||
        'Converted leads share of total leads',
    ],
    [
      'Deals',
      'Orders Won from Marketing',
      formatMetric(wonOrders, 'Orders'),
      capitalizeFirst(ordersClosed?.subtitle) ||
        'Won orders resulting from campaigns',
    ],
    [
      'Deals',
      'Orders Lost',
      formatMetric(lostOrders, 'Orders'),
      capitalizeFirst(ordersLost?.subtitle) || 'Dropped or lost campaign leads',
    ],
    [
      'Campaigns',
      'Active Campaigns Tracked',
      formatMetric(
        Array.isArray(campaignPerformance) ? campaignPerformance.length : 0,
        'Campaigns',
      ),
      'Running marketing campaign initiatives',
    ],
    [],
    [],
  ]

  // Table 2: Leads Generated by Channel (Source)
  const sourceItems = campaignLeads?.sources ?? []
  const totalSources = sourceItems.reduce(
    (sum, s) => sum + (Number(s.count) || 0),
    0,
  )

  summaryRows.push(
    ['LEADS GENERATED BY SOURCE CHANNEL'],
    ['Metric Group', 'Channel Source', 'Leads Count', 'Share of Total (%)'],
  )
  if (sourceItems.length === 0) {
    summaryRows.push([
      'Channel Source',
      'No sources recorded',
      '0 Leads',
      '0.0%',
    ])
  } else {
    sourceItems.forEach((source) => {
      const count = Number(source.count) || 0
      summaryRows.push([
        'Channel Source',
        capitalizeFirst(source.name),
        formatMetric(count, 'Leads'),
        `${calculatePercent(count, totalSources || totalLeads)} of total leads`,
      ])
    })
    summaryRows.push([
      'Summary Total',
      'TOTAL LEADS GENERATED',
      formatMetric(totalSources || totalLeads, 'Leads'),
      '100.0%',
    ])
  }

  summaryRows.push([], [])

  // Table 3: Pipeline Status Breakdown
  const statusItems = leadsManagement?.status?.items ?? []
  const totalStatus = statusItems.reduce(
    (sum, s) => sum + (Number(s.count) || 0),
    0,
  )

  summaryRows.push(
    ['LEADS PIPELINE STATUS BREAKDOWN'],
    [
      'Metric Group',
      'Pipeline Stage / Status',
      'Deals Count',
      'Share of Total (%)',
    ],
  )
  if (statusItems.length === 0) {
    summaryRows.push([
      'Pipeline Status',
      'No status data available',
      '0 Deals',
      '0.0%',
    ])
  } else {
    statusItems.forEach((item) => {
      const count = Number(item.count) || 0
      summaryRows.push([
        'Pipeline Status',
        capitalizeFirst(item.name),
        formatMetric(count, 'Deals'),
        `${calculatePercent(count, totalStatus)} of pipeline`,
      ])
    })
    summaryRows.push([
      'Summary Total',
      'TOTAL IN PIPELINE',
      formatMetric(totalStatus, 'Deals'),
      '100.0%',
    ])
  }

  summaryRows.push([], [])

  // Table 4: Lead Qualification Breakdown
  const qualItems = leadsManagement?.qualification?.items ?? []
  const totalQual = qualItems.reduce(
    (sum, s) => sum + (Number(s.count) || 0),
    0,
  )

  summaryRows.push(
    ['LEAD QUALIFICATION (WARMTH BREAKDOWN)'],
    ['Metric Group', 'Warmth Level', 'Leads Count', 'Share of Total (%)'],
  )
  if (qualItems.length === 0) {
    summaryRows.push([
      'Qualification',
      'No qualification data available',
      '0 Leads',
      '0.0%',
    ])
  } else {
    qualItems.forEach((item) => {
      const count = Number(item.count) || 0
      summaryRows.push([
        'Qualification',
        capitalizeFirst(item.name),
        formatMetric(count, 'Leads'),
        `${calculatePercent(count, totalQual)} of qualified leads`,
      ])
    })
    summaryRows.push([
      'Summary Total',
      'TOTAL QUALIFIED LEADS',
      formatMetric(totalQual, 'Leads'),
      '100.0%',
    ])
  }

  appendStyledSheet(workbook, 'Marketing Summary', summaryRows)

  // ---------------------------------------------------------------------------
  // 2. CAMPAIGN PERFORMANCE SHEET
  // ---------------------------------------------------------------------------
  const campaignRows = [
    ...createReportHeader({
      title: 'Campaign Performance Details',
      subtitle: 'Lead Generation Breakdown by Marketing Campaign and Channel',
      period,
      customRange,
      exportedAt,
    }),
    ['CAMPAIGN PERFORMANCE METRICS'],
    [
      '#',
      'Channel / Platform',
      'Campaign Name',
      'Leads Generated',
      'Share of Campaign Leads (%)',
    ],
  ]

  const totalCampaignLeads = (campaignPerformance ?? []).reduce(
    (sum, c) => sum + (Number(c.leads) || 0),
    0,
  )

  if (!Array.isArray(campaignPerformance) || campaignPerformance.length === 0) {
    campaignRows.push([
      '—',
      'No campaign performance data available',
      '—',
      0,
      '0.0%',
    ])
  } else {
    campaignPerformance.forEach((campaign, index) => {
      const leads = Number(campaign.leads) || 0
      campaignRows.push([
        index + 1,
        campaign.channel || '—',
        campaign.name || '—',
        leads,
        calculatePercent(leads, totalCampaignLeads || totalLeads),
      ])
    })

    campaignRows.push([
      'TOTAL',
      'All Channels',
      'All Campaigns',
      totalCampaignLeads,
      '100.0%',
    ])
  }

  appendStyledSheet(workbook, 'Campaign Performance', campaignRows)

  // ---------------------------------------------------------------------------
  // 3. PRODUCT & CONVERSION BREAKDOWN SHEET
  // ---------------------------------------------------------------------------
  const isWonBySource = Boolean(ordersClosed?.sources?.length)
  const isLostBySource = Boolean(ordersLost?.sources?.length)

  const wonItems = isWonBySource
    ? ordersClosed.sources
    : (ordersClosed?.products ?? [])

  const lostItems = isLostBySource
    ? ordersLost.sources
    : (ordersLost?.products ?? [])

  const totalWonCount = wonItems.reduce(
    (sum, item) => sum + (Number(item.units ?? item.count) || 0),
    0,
  )
  const totalLostCount = lostItems.reduce(
    (sum, item) => sum + (Number(item.units ?? item.count) || 0),
    0,
  )

  const breakdownRows = [
    ...createReportHeader({
      title: 'Orders Won & Lost Breakdown',
      subtitle:
        'Conversions and lost orders categorized by product and channel',
      period,
      customRange,
      exportedAt,
    }),
    ['ORDERS WON BREAKDOWN'],
    [
      isWonBySource ? 'Channel / Source' : 'Product Name',
      'Won Units / Orders',
      'Share of Won (%)',
    ],
  ]

  if (wonItems.length === 0) {
    breakdownRows.push(['No won orders recorded', 0, '0.0%'])
  } else {
    wonItems.forEach((item) => {
      const count = Number(item.units ?? item.count) || 0
      breakdownRows.push([
        item.name,
        count,
        calculatePercent(count, totalWonCount),
      ])
    })
    breakdownRows.push(['TOTAL WON', totalWonCount, '100.0%'])
  }

  breakdownRows.push(
    [],
    [],
    ['ORDERS LOST BREAKDOWN'],
    [
      isLostBySource ? 'Channel / Source' : 'Product Name',
      'Lost Units / Orders',
      'Share of Lost (%)',
    ],
  )

  if (lostItems.length === 0) {
    breakdownRows.push(['No lost orders recorded', 0, '0.0%'])
  } else {
    lostItems.forEach((item) => {
      const count = Number(item.units ?? item.count) || 0
      breakdownRows.push([
        item.name,
        count,
        calculatePercent(count, totalLostCount),
      ])
    })
    breakdownRows.push(['TOTAL LOST', totalLostCount, '100.0%'])
  }

  appendStyledSheet(workbook, 'Product Conversion Breakdown', breakdownRows)

  // ---------------------------------------------------------------------------
  // Write Workbook
  // ---------------------------------------------------------------------------
  const stamp = new Date().toISOString().slice(0, 10)
  XLSX.writeFile(workbook, `marketing-dashboard-${stamp}.xlsx`)
}
